// Pickups tab: upcoming bookings (reschedule/cancel by token) + schedule a new
// pickup from the customer's pickable orders. All email-keyed via the session.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import type { CustomerOrders, PendingPickup, PickupOrder, Slot } from "../lib/types";
import { Button, Card, EmptyState, ScreenHeader, Spinner } from "./ui";
import { SlotPicker } from "./SlotPicker";
import { formatDate } from "../lib/format";

function orderId(o: PickupOrder): string {
  return String(o["invoice-id"] || "");
}
function orderDate(o: PickupOrder): string {
  const raw = o["-date"] || o["date-sortable"] || "";
  return String(raw);
}

export function Pickups() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<"list" | "schedule" | "reschedule">("list");
  const [rescheduleToken, setRescheduleToken] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const pending = useQuery({
    queryKey: ["pickups", "pending"],
    queryFn: () => api.get<{ pending: PendingPickup[] }>("/api/pickups/pending"),
  });
  const orders = useQuery({
    queryKey: ["pickups", "orders"],
    queryFn: () => api.get<CustomerOrders>("/api/pickups/orders"),
  });

  const scheduleM = useMutation({
    mutationFn: (slot: Slot) =>
      api.post("/api/pickups/schedule", {
        orderIds: Array.from(selected),
        pickupTime: slot.date,
        customerName: orders.data?.customer?.name || "",
        customerId: orders.data?.customer?.id || "",
      }),
    onSuccess: () => {
      setBanner("Pickup scheduled — we'll email your confirmation.");
      setSelected(new Set());
      setMode("list");
      qc.invalidateQueries({ queryKey: ["pickups"] });
    },
  });

  const rescheduleM = useMutation({
    mutationFn: (slot: Slot) =>
      api.post("/api/pickups/reschedule", { token: rescheduleToken, newPickupTime: slot.date }),
    onSuccess: () => {
      setBanner("Pickup moved.");
      setRescheduleToken(null);
      setMode("list");
      qc.invalidateQueries({ queryKey: ["pickups"] });
    },
  });

  const cancelM = useMutation({
    mutationFn: (token: string) => api.post("/api/pickups/cancel", { token }),
    onSuccess: () => {
      setBanner("Pickup canceled.");
      qc.invalidateQueries({ queryKey: ["pickups"] });
    },
  });

  const pickable = (orders.data?.orders || []).filter((o) => o.canPickup && orderId(o));

  // ── Slot-picking sub-screens ──────────────────────────────────────
  if (mode === "schedule" || mode === "reschedule") {
    const active = mode === "schedule" ? scheduleM : rescheduleM;
    return (
      <div className="pb-4">
        <ScreenHeader
          title={mode === "schedule" ? "Pick a time" : "New time"}
          subtitle={mode === "schedule" ? `${selected.size} order${selected.size === 1 ? "" : "s"}` : undefined}
          right={
            <button
              className="text-[14px] text-ink-muted"
              onClick={() => {
                setMode("list");
                setRescheduleToken(null);
              }}
            >
              Cancel
            </button>
          }
        />
        <div className="px-4">
          <SlotPicker
            confirming={active.isPending}
            onPick={(slot) => (mode === "schedule" ? scheduleM.mutate(slot) : rescheduleM.mutate(slot))}
          />
          {active.isError && (
            <p className="text-sev-error text-sm mt-3">
              {active.error instanceof ApiError ? active.error.message : "Couldn't book that time."}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── List screen ───────────────────────────────────────────────────
  return (
    <div className="pb-4">
      <ScreenHeader title="Pickups" subtitle="Schedule and manage lab pickups" />

      {banner && (
        <div className="mx-4 mb-3 px-4 py-2.5 rounded-xl bg-navy-soft text-navy text-[13px] flex items-center justify-between">
          <span>{banner}</span>
          <button onClick={() => setBanner(null)} aria-label="Dismiss">
            <i className="ph ph-x" />
          </button>
        </div>
      )}

      {/* Upcoming */}
      <section className="px-4 mb-5">
        <h2 className="text-[13px] font-semibold uppercase tracking-wide text-ink-subtle px-1 mb-2">Upcoming</h2>
        {pending.isLoading ? (
          <Spinner />
        ) : pending.isError ? (
          <p className="text-sev-error text-sm px-1">Couldn't load your pickups. Pull to refresh or try again.</p>
        ) : pending.data?.pending?.length ? (
          <div className="flex flex-col gap-2.5">
            {pending.data.pending.map((p) => (
              <Card key={p.id} className="p-4 flex flex-col gap-3">
                <div>
                  <div className="text-ink font-medium">{p.display}</div>
                  <div className="text-[12px] text-ink-subtle capitalize">{p.pickup_type} pickup</div>
                  {p.can_reschedule && p.reschedule_deadline ? (
                    <div className="text-[11px] text-ink-faint">Reschedule until {formatDate(p.reschedule_deadline) || p.reschedule_deadline}</div>
                  ) : !p.can_reschedule ? (
                    <div className="text-[11px] text-ink-faint">This pickup can no longer be changed online.</div>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    className="flex-1 h-10"
                    disabled={!p.can_reschedule}
                    onClick={() => {
                      setRescheduleToken(p.reschedule_token);
                      setMode("reschedule");
                    }}
                  >
                    Reschedule
                  </Button>
                  <Button
                    variant="ghost"
                    className="h-10 text-sev-error"
                    disabled={cancelM.isPending}
                    onClick={() => {
                      if (confirm("Cancel this pickup?")) cancelM.mutate(p.reschedule_token);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <p className="text-sm text-ink-subtle px-1">No upcoming pickups.</p>
        )}
      </section>

      {/* Schedule new */}
      <section className="px-4">
        <h2 className="text-[13px] font-semibold uppercase tracking-wide text-ink-subtle px-1 mb-2">
          Ready to pick up
        </h2>
        {orders.isLoading ? (
          <Spinner />
        ) : orders.isError ? (
          <p className="text-sev-error text-sm px-1">Couldn't load your orders. Pull to refresh or try again.</p>
        ) : pickable.length ? (
          <>
            <div className="flex flex-col gap-2.5">
              {pickable.map((o) => {
                const id = orderId(o);
                const on = selected.has(id);
                return (
                  <button
                    key={id}
                    onClick={() => {
                      const next = new Set(selected);
                      on ? next.delete(id) : next.add(id);
                      setSelected(next);
                    }}
                    className={`text-left p-4 rounded-2xl border flex items-center gap-3 ${
                      on ? "border-navy bg-navy-soft" : "border-line bg-surface"
                    }`}
                  >
                    <span
                      className={`h-5 w-5 rounded-md border flex items-center justify-center ${
                        on ? "bg-navy border-navy text-white" : "border-line-strong"
                      }`}
                    >
                      {on && <i className="ph-bold ph-check text-[12px]" />}
                    </span>
                    <span className="flex-1">
                      <span className="block text-ink font-medium">{o.formattedReturn || "Order"}</span>
                      <span className="block text-[12px] text-ink-subtle">
                        {orderDate(o)} · {o.pickupReason}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            {selected.size > 0 && (
              <div className="sticky bottom-2 mt-3">
                <Button className="w-full" onClick={() => setMode("schedule")}>
                  Choose a time · {selected.size} order{selected.size === 1 ? "" : "s"}
                </Button>
              </div>
            )}
          </>
        ) : (
          <EmptyState
            icon="ph-package"
            title="Nothing ready to pick up"
            hint="Orders show up here once they're developed and ready to collect."
          />
        )}
      </section>
    </div>
  );
}
